import Phaser from 'phaser';
import { GAME_WIDTH, GAME_HEIGHT, WALL_X, WALL_WIDTH } from '../config';
import { StickFigureGraphics } from '../rendering/StickFigureGraphics';
import { Wall } from './Wall';

export type EnemyProjectileType = 'arrow' | 'sling_stone' | 'rock';

export class EnemyArrow {
  scene: Phaser.Scene;
  x: number;
  y: number;
  startY: number;
  damage: number;
  speed: number;
  graphics: Phaser.GameObjects.Graphics;
  isDestroyed: boolean = false;
  angle: number;
  velocityX: number;
  velocityY: number;
  targetX: number;
  targetY: number;
  projectileType: EnemyProjectileType;
  lobTime: number = 0;
  lobDuration: number = 0;

  constructor(scene: Phaser.Scene, x: number, y: number, targetX: number, targetY: number, damage: number, projectileType: EnemyProjectileType = 'arrow') {
    this.scene = scene;
    this.x = x;
    this.y = y;
    this.startY = y;
    this.targetX = targetX;
    this.targetY = targetY;
    this.damage = damage;
    this.projectileType = projectileType;
    this.speed = projectileType === 'sling_stone' ? 624 : (projectileType === 'rock' ? 400 : 520);
    this.graphics = scene.add.graphics();

    const dx = targetX - x;
    const dy = targetY - y;
    const dist = Math.sqrt(dx * dx + dy * dy);
    this.angle = Math.atan2(dy, dx);
    this.velocityX = (dx / dist) * this.speed;
    this.velocityY = (dy / dist) * this.speed;

    if (projectileType === 'rock') {
      this.lobDuration = dist / this.speed;
    }
  }

  update(_time: number, delta: number, wall: Wall): boolean {
    if (this.isDestroyed) return false;

    const dt = delta / 1000;

    if (this.projectileType === 'rock') {
      this.lobTime += dt;
      this.x += this.velocityX * dt;
      // Arc formula: y = startY + vY*t - 4*h*(t/d)*(1 - t/d)
      // where h is peak height
      const t = this.lobTime;
      const d = this.lobDuration;
      const peakHeight = 150; // How high the rock arcs
      if (d > 0) {
        // Linearly interpolate baseline Y
        const baselineY = this.startY + (this.targetY - this.startY) * (t / d);
        // Add parabolic arc (subtract because Y is down)
        const arcPos = 4 * peakHeight * (t / d) * (1 - t / d);
        this.y = baselineY - arcPos;
      }
    } else {
      this.x += this.velocityX * dt;
      this.y += this.velocityY * dt;
    }

    // Check if arrow hit the wall zone
    const wallRight = WALL_X + WALL_WIDTH;
    if (this.x <= wallRight) {
      // For rocks, make sure they actually finished their lob (or hit wall height)
      if (this.projectileType !== 'rock' || this.lobTime >= this.lobDuration * 0.9) {
        wall.takeDamage(this.damage);
        this.destroy();
        return false;
      }
    }

    // Out of bounds
    if (this.x < -50 || this.x > GAME_WIDTH + 50 || this.y < -50 || this.y > GAME_HEIGHT + 50) {
      this.destroy();
      return false;
    }

    this.draw();
    return true;
  }

  draw(): void {
    this.graphics.clear();
    if (this.projectileType === 'sling_stone') {
      StickFigureGraphics.drawSlingStone(this.graphics, this.x, this.y);
    } else if (this.projectileType === 'rock') {
      // Draw a large boulder rolling through the air
      this.graphics.fillStyle(0x555555, 1);
      this.graphics.fillCircle(this.x, this.y, 10);
      this.graphics.fillStyle(0x777777, 1);
      this.graphics.fillCircle(this.x - 3, this.y - 3, 6);
      this.graphics.fillStyle(0x333333, 1);
      this.graphics.fillCircle(this.x + 4, this.y + 4, 3);
    } else {
      // Reuse the arrow drawing but with a red-tinted arrowhead
      const len = 16;
      // Shaft
      this.graphics.lineStyle(1.5, 0x8b4513, 1);
      this.graphics.beginPath();
      this.graphics.moveTo(this.x - Math.cos(this.angle) * len / 2, this.y - Math.sin(this.angle) * len / 2);
      this.graphics.lineTo(this.x + Math.cos(this.angle) * len / 2, this.y + Math.sin(this.angle) * len / 2);
      this.graphics.strokePath();
      // Red arrowhead (enemy arrows)
      const tipX = this.x + Math.cos(this.angle) * len / 2;
      const tipY = this.y + Math.sin(this.angle) * len / 2;
      const perpAngle = this.angle + Math.PI / 2;
      this.graphics.fillStyle(0xcc4444, 1);
      this.graphics.fillTriangle(
        tipX + Math.cos(this.angle) * 4, tipY + Math.sin(this.angle) * 4,
        tipX + Math.cos(perpAngle) * 3, tipY + Math.sin(perpAngle) * 3,
        tipX - Math.cos(perpAngle) * 3, tipY - Math.sin(perpAngle) * 3
      );
      // Fletching
      const tailX = this.x - Math.cos(this.angle) * len / 2;
      const tailY = this.y - Math.sin(this.angle) * len / 2;
      this.graphics.lineStyle(1, 0xcc4444, 0.8);
      this.graphics.beginPath();
      this.graphics.moveTo(tailX, tailY);
      this.graphics.lineTo(tailX + Math.cos(perpAngle) * 3, tailY + Math.sin(perpAngle) * 3);
      this.graphics.moveTo(tailX, tailY);
      this.graphics.lineTo(tailX - Math.cos(perpAngle) * 3, tailY - Math.sin(perpAngle) * 3);
      this.graphics.strokePath();
    }
  }

  destroy(): void {
    this.isDestroyed = true;
    this.graphics.destroy();
  }
}
